import React, { useCallback } from 'react'
import { ClapperBoardIcon } from '@/atoms/Icons/ClapperBoardIcon'
import { EpisodeIcon } from '@/atoms/Icons/EpisodeIcon'
import { TorchIcon } from '@/atoms/Icons/TorchIcon'
import { HeadingMD, ParagraphMD, CaptionMD, TitleMD } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../TorchSubmitButton'

interface SubmissionCardProps {
  icon: React.ReactNode
  title: string
  description: string
}

const SubmissionCard: React.FC<SubmissionCardProps> = ({ icon, title, description }) => {
  return (
    <div className="flex min-w-[280px] flex-1 flex-col rounded-2xl bg-white p-8 shadow-lg">
      <div className="mb-4">{icon}</div>
      <TitleMD weight="bold" color="core-gray-950" className="mb-4">
        {title}
      </TitleMD>
      <ParagraphMD weight="normal" color="core-gray-700" className="leading-relaxed">
        {description}
      </ParagraphMD>
    </div>
  )
}

export const AcceptableSubmissionsSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')

  const iconSize = innerWidth > 1024 ? 32 : 24

  const handleGetStartedClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="w-full bg-gray-900 bg-cover bg-center bg-no-repeat py-16 md:py-24"
      style={{
        backgroundImage: 'linear-gradient(180deg, #F5F5F4 0%, #C3C1C0 100%)',
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-12 lg:px-[120px]">
        <HeadingMD
          weight="bold"
          color="core-gray-950"
          className="md:photon-heading-xl lg:photon-display mb-12 text-center"
        >
          {t('acceptableSubmissions', 'Acceptable Submissions')}
        </HeadingMD>

        <div className="mb-12 flex flex-col gap-6 md:flex-row md:gap-8">
          <SubmissionCard
            icon={<TorchIcon size={iconSize} color="core-gray-950" />}
            title={t('torches', 'Torches')}
            description={t(
              'torchDescription',
              'A Torch is a 5 minute or longer continuous sequence that acts as a prototype for your film or series.',
            )}
          />
          <SubmissionCard
            icon={<EpisodeIcon size={iconSize} color="core-gray-950" />}
            title={t('episodes', 'Episodes')}
            description={t(
              'episodeDescription',
              "If you've completed at least one episode, start by submitting a full episode for voting—just be sure to cut off any credits.",
            )}
          />
          <SubmissionCard
            icon={<ClapperBoardIcon size={iconSize} color="core-gray-950" />}
            title={t('completedFilms', 'Completed Films')}
            description={t(
              'completedFilmsDescription',
              "There are two steps: First, submit a short clip as a Torch. If it passes the vote, you'll then submit your full film.",
            )}
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          <TorchSubmitButton handleSubmitClicked={handleGetStartedClicked} isDark={false} />
          <CaptionMD weight="normal">{t('applicationFee', '$250 application fee')}</CaptionMD>
        </div>
      </div>
    </section>
  )
}
